import jsPDF from 'jspdf';
import 'jspdf-autotable';
import patientDobToAge from 'utils/patientDobToAge';
import sortInsurance from 'utils/sortInsurance';
import getBusinessAddress from 'utils/print/getBusinessAddress';

const templateToPDF = ({
  inputPDF, template, patient, order, business, signature, values = {},
}) => new Promise((resolve) => {
  const lineHeight = 1.0;
  const margin = 10;
  const ptsPerMm = 2.834646;
  const fontSize = 10;
  const titleFontSize = 16;
  const fontType = 'normal';
  const textColor = 0;
  const drawColor = 0;
  const lineHeightMm = fontSize * (lineHeight / ptsPerMm);
  const format = 'a4';
  // eslint-disable-next-line new-cap
  const doc = inputPDF || new jsPDF({
    orientation: 'p',
    unit: 'mm',
    format,
    lineHeight,
  });
  if (inputPDF) {
    doc.addPage(format);
  }
  doc.setFont('times');
  let y = margin;
  let x = margin;
  const pageContentWidth = doc.internal.pageSize.getWidth() - (margin * 2);
  let rowEndY = null;
  let maxWidth = pageContentWidth;

  let insurances;
  let prescribers;
  let vitals;
  let diagnoses;
  if (patient) {
    prescribers = patient.Prescriber;
    vitals = patient.Vitals
    diagnoses = patient.Diagnosis;
    insurances = patient['Insurance Information'].filter(item =>
      item['Insurance Type'] !== 'Self');
  }
  if (order) {
    prescribers = [order.Prescriber];
    vitals = order.Vitals;
    diagnoses = order.Diagnosis;
    insurances = order.Insurance.filter(item =>
      item['Insurance Type'] !== 'Self');
  }

  const setDefaults = () => {
    doc.setFontSize(fontSize);
    doc.setFontType(fontType);
    doc.setTextColor(textColor);
    doc.setDrawColor(drawColor);
  };
  const addY = (newY) => {
    if ((y + newY) < doc.internal.pageSize.getHeight() - (margin * 2)) {
      y += newY;
    } else {
      doc.addPage();
      y = margin;
    }
  };
  const setX = () => {
    x = rowEndY ? (pageContentWidth / 2) + (margin * 1.5) : margin;
  };
  const setMaxWidth = (gridWidth) => {
    maxWidth = gridWidth === 6 ? (pageContentWidth - margin) / 2 : pageContentWidth;
  };
  const renderTitle = () => {
    doc.setFontSize(titleFontSize);
    doc.setFontType('bold');
    x = (doc.internal.pageSize.getWidth() / 2);
    maxWidth = pageContentWidth * 0.75;
    const text = doc.splitTextToSize(values.Name || template.Name, maxWidth);
    text.forEach((line) => {
      addY(titleFontSize * (lineHeight / ptsPerMm));
      doc.text(line, x, y, null, null, 'center');
    });
    addY(margin / 4);
  };
  const renderTextbox = (question) => {
    setDefaults();
    setX();
    setMaxWidth(question['Grid Width']);
    const text = doc.splitTextToSize(question.Text, maxWidth);
    text.forEach((line) => {
      addY(lineHeightMm);
      doc.text(line, x, y);
    });
    addY(margin / 4);
  };
  const renderMultiLine = (question) => {
    setDefaults();
    setX();
    setMaxWidth(question['Grid Width']);
    const text = question.Text ? doc.splitTextToSize(question.Text, maxWidth) : null;
    const answer = question.Answer
      ? doc.splitTextToSize(question.Answer, maxWidth - (margin / 2)) : ['', ''];
    if (text) {
      text.forEach((line) => {
        addY(lineHeightMm);
        doc.text(line, x, y);
      });
      addY(margin / 4);
    } else {
      addY(lineHeightMm / 4);
    }
    doc.rect(x, y, maxWidth, lineHeightMm * answer.length);
    answer.forEach((line) => {
      addY(lineHeightMm);
      doc.text(line, x + (margin / 4), y - 1.5);
    });
    addY(margin / 4);
  };
  const renderCheckbox = (question) => {
    setDefaults();
    setX();
    setMaxWidth(question['Grid Width']);
    const text = question.Text ? doc.splitTextToSize(question.Text, maxWidth) : null;
    if (text) {
      text.forEach((line) => {
        addY(lineHeightMm);
        doc.text(line, x, y);
      });
      addY(margin / 4);
    } else {
      addY(lineHeightMm / 4);
    }
    question.Choices.forEach((choice) => {
      const choiceText = doc.splitTextToSize(choice, maxWidth - (lineHeightMm * 1.5));
      doc.rect(x, y + (lineHeightMm * 0.25), lineHeightMm, lineHeightMm);
      if (question.Answer
        && question.Answer[choice]
        && question.Answer[choice] === true) {
        doc.rect(x + 1, y + (lineHeightMm * 0.25) + 1, lineHeightMm - 2, lineHeightMm - 2, 'F');
      }
      choiceText.forEach((line) => {
        doc.text(line, x + (lineHeightMm * 1.5), y + lineHeightMm);
        addY(lineHeightMm);
      });
      addY(margin / 6);
    });
  };
  const renderRadioDropDown = (question) => {
    setDefaults();
    setX();
    setMaxWidth(question['Grid Width']);
    const text = question.Text ? doc.splitTextToSize(question.Text, maxWidth) : null;
    if (text) {
      text.forEach((line) => {
        addY(lineHeightMm);
        doc.text(line, x, y);
      });
      addY(margin / 4);
    } else {
      addY(lineHeightMm / 4);
    }
    question.Choices.forEach((choice) => {
      const choiceText = doc.splitTextToSize(choice, maxWidth - (lineHeightMm * 1.5));
      doc.rect(x, y + (lineHeightMm * 0.25), lineHeightMm, lineHeightMm);
      if (choice === question.Answer) {
        doc.rect(x + 1, y + (lineHeightMm * 0.25) + 1, lineHeightMm - 2, lineHeightMm - 2, 'F');
      }
      choiceText.forEach((line) => {
        doc.text(line, x + (lineHeightMm * 1.5), y + lineHeightMm);
        addY(lineHeightMm);
      });
      addY(margin / 6);
    });
  };
  const renderDate = (question) => {
    setDefaults();
    setX();
    setMaxWidth(question['Grid Width']);
    const text = question.Text ? doc.splitTextToSize(question.Text, maxWidth) : null;
    const answer = question.Answer
      ? doc.splitTextToSize(question.Answer, maxWidth - (margin / 2)) : ['_ _/_ _/_ _ _ _'];
    const boxWidth = (doc.getTextWidth(answer[0])) + (margin / 2);
    if (text) {
      text.forEach((line) => {
        addY(lineHeightMm);
        doc.text(line, x, y);
      });
      addY(margin / 4);
    } else {
      addY(lineHeightMm / 4);
    }
    doc.rect(x, y, boxWidth, lineHeightMm);
    addY(lineHeightMm);
    doc.text(answer[0], x + (margin / 4), y - 1.5);
    addY(margin / 4);
  };
  const renderSignature = (question) => {
    setDefaults();
    setX();
    setMaxWidth(question['Grid Width']);
    doc.setFontStyle('italic');
    const text = question.Text ? doc.splitTextToSize(question.Text, maxWidth) : null;
    const lineWidth = 30;
    if (text) {
      text.forEach((line) => {
        addY(lineHeightMm);
        doc.text(line, x, y);
      });
    }
    addY(lineHeightMm / 2);
    if (question.Answer || signature) {
      doc.addImage(question.Answer || signature, 'PNG', x, y, 30, 8);
      addY(8);
    }
    doc.line(x, y, x + lineWidth, y);
    doc.line((x + maxWidth) - lineWidth, y, x + maxWidth, y);
    addY(lineHeightMm);
    doc.setFontStyle('normal');
    doc.text('Signature', x + (lineWidth / 2), y, null, null, 'center');
    doc.text('Date', (x + maxWidth) - (lineWidth / 2), y, null, null, 'center');
    addY(margin / 4);
  };
  const renderPatientGeneral = (question) => {
    setDefaults();
    setX();
    setMaxWidth(question['Grid Width']);
    addY(lineHeightMm);
    const title = 'Patient Information';
    const columns = [
      { title: '', dataKey: 'label' },
      { title: '', dataKey: 'value' },
    ];
    let dateOfBirthValue = '';
    if (patient) {
      dateOfBirthValue = patient['Date of Death']
        ? patient['Date of Birth']
        : `${patient['Date of Birth']}, ${patientDobToAge(patient['Date of Birth'])}`;
    }
    const rows = [
      ...[{
        label: 'Patient Name:',
        value: patient ? `${patient['Last Name']}, ${patient['First Name']}` : '',
      },
      {
        label: patient && patient['Date of Death']
          ? 'Date of Birth'
          : 'Date of Birth, Age',
        value: dateOfBirthValue,
      }],
      ...patient && patient['Date of Death'] ? [{
        label: 'Date of Death',
        value: patient['Date of Death'],
      }] : [],
      ...[{
        label: 'Gender:',
        value: patient ? patient.Gender : '',
      }],
    ];
    doc.text(title, x, y);
    addY(margin / 8);
    const titleWidth = doc.getTextWidth(title);
    doc.line(x, y, x + titleWidth, y);
    addY(margin / 4);
    doc.autoTable(columns, rows, {
      theme: 'plain',
      startY: y,
      showHeader: 'never',
      margin: [0, 0, 0, x],
      tableWidth: maxWidth,
      styles: {
        font: 'times',
        textColor,
        lineColor: drawColor,
        fontSize,
        cellPadding: [0, margin / 4, 0, margin / 4],
        columnWidth: 'wrap',
        overflow: 'linebreak',
      },
      columnStyles: {
        label: {
          fontStyle: 'bold',
          cellPadding: [0, margin / 4, 0, 0],
        },
      },
    });
    const table = doc.autoTable.previous;
    addY((table.finalY - y) + (margin / 4));
  };
  const renderPatientInsurance = (question) => {
    setDefaults();
    setX();
    setMaxWidth(question['Grid Width']);
    addY(lineHeightMm);
    if (patient || order) {
      if (insurances.length > 0) {
        const title = 'Patient Insurance';
        const columns = [
          { title: '', dataKey: 'payerName' },
          { title: '', dataKey: 'orderBenefits' },
          { title: '', dataKey: 'insuranceIDLabel' },
          { title: '', dataKey: 'insuranceID' },
        ];
        const rows = sortInsurance(insurances)
          .map(item => ({
            payerName: item['Insurance Info']['Payer Name'],
            orderBenefits: item['Order Benefits']
              ? item['Order Benefits'] : '',
            insuranceIDLabel: 'ID:',
            insuranceID: item['Insurance ID'],
          }));
        doc.text(title, x, y);
        addY(margin / 8);
        const titleWidth = doc.getTextWidth(title);
        doc.line(x, y, x + titleWidth, y);
        addY(margin / 4);
        doc.autoTable(columns, rows, {
          theme: 'plain',
          startY: y,
          showHeader: 'never',
          margin: [0, 0, 0, x],
          tableWidth: maxWidth,
          styles: {
            font: 'times',
            textColor,
            lineColor: drawColor,
            fontSize,
            cellPadding: [0, margin / 4, 0, margin / 4],
            columnWidth: 'wrap',
            overflow: 'linebreak',
          },
          columnStyles: {
            payerName: {
              fontStyle: 'bold',
              cellPadding: [0, margin / 4, 0, 0],
              ...question['Grid Width'] !== 12 ? { columnWidth: 'auto' } : {},
            },
            insuranceIDLabel: {
              fontStyle: 'bold',
              cellPadding: [0, 0, 0, margin / 4],
            },
            insuranceID: {
              halign: 'right',
              cellPadding: [0, 0, 0, margin / 4],
            },
          },
        });
        const table = doc.autoTable.previous;
        addY((table.finalY - y) + (margin / 4));
      } else {
        doc.text('Patient has no insurance', x, y);
        addY(margin / 4);
      }
    } else {
      doc.text('Patient insurance', x, y);
      addY(margin / 4);
    }
  };
  const renderPatientVitals = (question) => {
    setDefaults();
    setX();
    setMaxWidth(question['Grid Width']);
    addY(lineHeightMm);
    if (patient || order) {
      if (vitals.length > 0) {
        const title = 'Patient Vitals';
        const columns = [
          { title: '', dataKey: 'weightLabel' },
          { title: '', dataKey: 'weight' },
          { title: '', dataKey: 'heightLabel' },
          { title: '', dataKey: 'height' },
          { title: '', dataKey: 'bmiLabel' },
          { title: '', dataKey: 'bmi' },
          { title: '', dataKey: 'createAt' },
        ];
        const rows = vitals.map(item => ({
          weightLabel: question['Grid Width'] !== 12 ? 'W' : 'Weight',
          weight: `${item.Weight} lb`,
          heightLabel: question['Grid Width'] !== 12 ? 'H' : 'Height',
          height: `${item['Height Ft']}ft, ${item['Height In']}in`,
          bmiLabel: 'BMI',
          bmi: item.BMI,
          createAt: item['Create At'],
        }));
        doc.text(title, x, y);
        addY(margin / 8);
        const titleWidth = doc.getTextWidth(title);
        doc.line(x, y, x + titleWidth, y);
        addY(margin / 4);
        doc.autoTable(columns, rows, {
          theme: 'plain',
          startY: y,
          showHeader: 'never',
          margin: [0, 0, 0, x],
          tableWidth: maxWidth,
          styles: {
            font: 'times',
            textColor,
            lineColor: drawColor,
            fontSize,
            cellPadding: [0, margin / 4, 0, margin / 4],
            columnWidth: 'wrap',
            overflow: 'linebreak',
          },
          columnStyles: {
            weightLabel: {
              fontStyle: 'bold',
              cellPadding: 0,
            },
            heightLabel: {
              fontStyle: 'bold',
              cellPadding: [0, 0, 0, margin / 4],
            },
            bmiLabel: {
              fontStyle: 'bold',
              cellPadding: [0, 0, 0, margin / 4],
            },
            createAt: {
              cellPadding: [0, 0, 0, margin / 4],
            },
          },
        });
        const table = doc.autoTable.previous;
        addY((table.finalY - y) + (margin / 2));
      } else {
        doc.text('Patient has no vitals', x, y);
        addY(margin / 4);
      }
    } else {
      doc.text('Patient vitals', x, y);
      addY(margin / 4);
    }
  };
  const renderPatientPrescribers = (question) => {
    setDefaults();
    setX();
    setMaxWidth(question['Grid Width']);
    addY(lineHeightMm);
    if (patient || order) {
      if (prescribers.length > 0) {
        const title = 'Prescribers Information';
        const columns = [
          { title: '', dataKey: 'name' },
          ...question['Grid Width'] !== 12 ? [
            { title: '', dataKey: 'contacts' },
            { title: '', dataKey: 'licenseLabel' },
            { title: '', dataKey: 'license' },
          ] : [
            { title: '', dataKey: 'email' },
            { title: '', dataKey: 'phone' },
            { title: '', dataKey: 'npiLabel' },
            { title: '', dataKey: 'npi' },
            { title: '', dataKey: 'licLabel' },
            { title: '', dataKey: 'lic' },
          ],
        ];
        const rows = prescribers.reduce((accumulator, currentValue) => {
          if (question['Grid Width'] !== 12) {
            return [
              ...accumulator,
              ...[
                {
                  name: `${currentValue['First Name']} ${currentValue['Last Name']}`,
                  contacts: currentValue.Email || '',
                  licenseLabel: currentValue.NPI ? 'NPI: ' : '',
                  license: currentValue.NPI || '',
                },
                {
                  name: '',
                  contacts: currentValue.Phone || '',
                  licenseLabel: currentValue.LIC ? 'LIC: ' : '',
                  license: currentValue.LIC || '',
                },
              ],
            ];
          }
          return [
            ...accumulator,
            {
              name: `${currentValue['First Name']} ${currentValue['Last Name']}`,
              email: currentValue.Email || '',
              phone: currentValue.Phone || '',
              npiLabel: currentValue.NPI ? 'NPI:' : '',
              npi: currentValue.NPI || '',
              licLabel: currentValue.LIC ? 'LIC:' : '',
              lic: currentValue.LIC || '',
            },
          ];
        }, []);
        doc.text(title, x, y);
        addY(margin / 8);
        const titleWidth = doc.getTextWidth(title);
        doc.line(x, y, x + titleWidth, y);
        addY(margin / 4);
        doc.autoTable(columns, rows, {
          theme: 'plain',
          startY: y,
          showHeader: 'never',
          margin: [0, 0, 0, x],
          tableWidth: maxWidth,
          styles: {
            font: 'times',
            textColor,
            lineColor: drawColor,
            fontSize,
            cellPadding: [0, margin / 4, 0, margin / 4],
            columnWidth: 'wrap',
            overflow: 'linebreak',
          },
          columnStyles: {
            name: {
              fontStyle: 'bold',
              cellPadding: [0, margin / 4, 0, 0],
            },
            contacts: {
              columnWidth: 'auto',
            },
            npiLabel: {
              fontStyle: 'bold',
              cellPadding: [0, 0, 0, margin / 4],
            },
            licLabel: {
              fontStyle: 'bold',
              cellPadding: [0, 0, 0, margin / 4],
            },
            licenseLabel: {
              fontStyle: 'bold',
              cellPadding: [0, 0, 0, margin / 4],
            },
            license: {
              cellPadding: 0,
            },
          },
        });
        const table = doc.autoTable.previous;
        addY((table.finalY - y) + (margin / 2));
      } else {
        doc.text('Patient has no prescribers', x, y);
        addY(margin / 4);
      }
    } else {
      doc.text('Patient prescribers', x, y);
      addY(margin / 4);
    }
  };
  const renderPatientDiagnoses = (question) => {
    setDefaults();
    setX();
    setMaxWidth(question['Grid Width']);
    addY(lineHeightMm);
    if (patient || order) {
      if (diagnoses.length > 0) {
        const title = 'Diagnoses';
        const columns = [
          { title: '', dataKey: 'dxCode' },
          { title: '', dataKey: 'longDescription' },
        ];
        const rows = diagnoses.map(item => ({
          dxCode: item['DX Code'],
          longDescription: item['Long Description'],
        }));
        doc.text(title, x, y);
        addY(margin / 8);
        const titleWidth = doc.getTextWidth(title);
        doc.line(x, y, x + titleWidth, y);
        addY(margin / 4);
        doc.autoTable(columns, rows, {
          theme: 'plain',
          startY: y,
          showHeader: 'never',
          margin: [0, 0, 0, x],
          tableWidth: maxWidth,
          styles: {
            font: 'times',
            textColor,
            lineColor: drawColor,
            fontSize,
            cellPadding: [0, margin / 4, 0, margin / 4],
            columnWidth: 'wrap',
            overflow: 'linebreak',
          },
          columnStyles: {
            dxCode: {
              fontStyle: 'bold',
              cellPadding: [0, margin / 4, 0, 0],
            },
            longDescription: {
              columnWidth: 'auto',
              cellPadding: [0, 0, 0, margin / 4],
            },
          },
        });
        const table = doc.autoTable.previous;
        addY((table.finalY - y) + (margin / 2));
      } else {
        doc.text('Patient has no diagnoses', x, y);
        addY(margin / 4);
      }
    } else {
      doc.text('Patient diagnoses', x, y);
      addY(margin / 4);
    }
  };
  const renderHCPCSCodes = (question) => {
    setDefaults();
    setX();
    setMaxWidth(question['Grid Width']);
    addY(lineHeightMm);
    if (order) {
      const orderLine = order['Order Line'];
      if (orderLine.length > 0) {
        const title = 'HCPCS Codes';
        const columns = [
          { title: '', dataKey: 'HCPCSCode' },
          { title: '', dataKey: 'description' },
          { title: '', dataKey: 'units' },
        ];
        const rows = orderLine.map(item => ({
          HCPCSCode: item['HCPC S Code']['HCPC S Code'],
          description: item['Description'],
          units: `*${item.Units}`,
        }));
        doc.text(title, x, y);
        addY(margin / 8);
        const titleWidth = doc.getTextWidth(title);
        doc.line(x, y, x + titleWidth, y);
        addY(margin / 4);
        doc.autoTable(columns, rows, {
          theme: 'plain',
          startY: y,
          showHeader: 'never',
          margin: [0, 0, 0, x],
          tableWidth: maxWidth,
          styles: {
            font: 'times',
            textColor,
            lineColor: drawColor,
            fontSize,
            cellPadding: [0, margin / 4, 0, margin / 4],
            columnWidth: 'wrap',
            overflow: 'linebreak',
          },
          columnStyles: {
            HCPCSCode: {
              fontStyle: 'bold',
              cellPadding: [0, margin / 4, 0, 0],
            },
          },
        });
        const table = doc.autoTable.previous;
        addY((table.finalY - y) + (margin / 2));
      }
    }
  };

  const renderCompanyInformation = (question) => {
    setDefaults();
    setX();
    setMaxWidth(question['Grid Width']);
    addY(lineHeightMm);
    const title = 'Company Information';
    const columns1 = [
      { title: '', dataKey: 'label1' },
      { title: '', dataKey: 'value1' },
      ...business && business['Company Identify'] && business['Company Identify'].ID ? [
        { title: '', dataKey: 'label2' },
        { title: '', dataKey: 'value2' },
      ] : [], 
    ];
    const rows1 = [
      {
        label1: 'Company Name:',
        value1: business && business['Company Info'] && business['Company Info'].ID
          ? business['Company Info']['Company Name'] : '',
        ...business && business['Company Identify'] && business['Company Identify'].ID ? {
          label2: 'NPI:',
          value2: business && business['Company Identify'] && business['Company Identify'].ID
            ? business['Company Identify']['Practice NPI'] : '',
        } : {}, 
      },
    ];
    doc.text(title, x, y);
    addY(margin / 8);
    const titleWidth = doc.getTextWidth(title);
    doc.line(x, y, x + titleWidth, y);
    addY(margin / 4);
    doc.autoTable(columns1, rows1, {
      theme: 'plain',
      startY: y,
      showHeader: 'never',
      margin: [0, 0, 0, x],
      tableWidth: maxWidth,
      styles: {
        font: 'times',
        textColor,
        lineColor: drawColor,
        fontSize,
        cellPadding: [0, margin / 4, 0, margin / 4],
        columnWidth: 'wrap',
        overflow: 'linebreak',
      },
      columnStyles: {
        label1: {
          fontStyle: 'bold',
          cellPadding: [0, margin / 4, 0, 0],
        },
        value1: {
          columnWidth: 'auto',
          cellPadding: [0, 0, 0, margin / 4],
        },
        label2: {
          fontStyle: 'bold',
          cellPadding: [0, margin / 4, 0, 0],
        },
      },
    });
    let table = doc.autoTable.previous;
    addY((table.finalY - y) + (margin / 4));
    if (business && business['Company Address'] && business['Company Address'].ID) {
      const columns2 = [
        { title: '', dataKey: 'label1' },
        { title: '', dataKey: 'value1' },
      ];
      const rows2 = [
        {
          label1: 'Address:',
          value1: business && business['Company Address'] && business['Company Address'].ID
            ? getBusinessAddress({ business }) : '',
        },
      ];
      doc.autoTable(columns2, rows2, {
        theme: 'plain',
        startY: y,
        showHeader: 'never',
        margin: [0, 0, 0, x],
        tableWidth: maxWidth,
        styles: {
          font: 'times',
          textColor,
          lineColor: drawColor,
          fontSize,
          cellPadding: [0, margin / 4, 0, margin / 4],
          columnWidth: 'wrap',
          overflow: 'linebreak',
        },
        columnStyles: {
          label1: {
            fontStyle: 'bold',
            columnWidth: margin * 2.8,
            cellPadding: [0, margin / 4, 0, 0],
          },
          value1: {
            columnWidth: 'auto',
            cellPadding: [0, 0, 0, margin / 4],
          },
        },
      });
      table = doc.autoTable.previous;
      addY((table.finalY - y) + (margin / 4));
    }
  };

  const renderCompanyLogo = (question) => {
    setDefaults();
    setX();
    if (business && business.Image && business.Image.ID) {
      doc.addImage(`${business.Image['File Url']}${business.Image['File Name']}`, 'PNG', x, y, 35, 14);
      addY(8);
    }
  };

  renderTitle();
  template.Questions.forEach((question, index, questions) => {
    const startY = y;
    const data = {
      ...question,
      Answer: values.Questions ? values.Questions[index] : null,
    };
    switch (question['Question Type']) {
      case 'Textbox':
        renderTextbox(data);
        break;
      case 'MultiLine':
        renderMultiLine(data);
        break;
      case 'Checkbox':
        renderCheckbox(data);
        break;
      case 'Radio':
        renderRadioDropDown(data);
        break;
      case 'DropDown':
        renderRadioDropDown(data);
        break;
      case 'Date':
        renderDate(data);
        break;
      case 'Signature':
        renderSignature(data);
        break;
      case 'Patient General':
        renderPatientGeneral(data);
        break;
      case 'Patient Insurance':
        renderPatientInsurance(data);
        break;
      case 'Patient Vitals':
        renderPatientVitals(data);
        break;
      case 'Patient Prescribers':
        renderPatientPrescribers(data);
        break;
      case 'Patient Diagnosis':
        renderPatientDiagnoses(data);
        break;
      case 'HCPCS Codes':
        renderHCPCSCodes(data);
        break;
      case 'Company Information':
        renderCompanyInformation(data);
        break;
      case 'Company Logo':
        renderCompanyLogo(data);
        break;
      default:
        break;
    }

    if (!rowEndY
        && question['Grid Width'] === 6
        && questions[index + 1]
        && questions[index + 1]['Grid Width'] === 6) {
      rowEndY = y;
      y = startY;
    } else {
      if (rowEndY > y) {
        y = rowEndY;
      }
      rowEndY = null;
    }
  });

  resolve(doc);
});

export default templateToPDF;



// WEBPACK FOOTER //
// ./src/utils/templateToPDF.js